/**
 * LandingAgeonDifference — conventional model vs AGEON model.
 */
import {
    Ban,
    BarChart2,
    Check,
    ClipboardCheck,
    Cpu,
    LineChart,
    Network,
    RefreshCcw,
    Repeat,
    ShoppingBag,
    Target,
    UserCheck,
    X,
} from 'lucide-react';

const CONVENTIONAL = [
    { label: 'Session selling', icon: ShoppingBag },
    { label: 'Isolated devices', icon: Cpu },
    { label: 'No baseline', icon: Ban },
    { label: 'Weak outcome tracking', icon: BarChart2 },
    { label: 'Low member continuity', icon: RefreshCcw },
];

const AGEON_MODEL = [
    { label: 'Assessment-led entry', icon: ClipboardCheck },
    { label: 'AGEON Score baseline', icon: Target },
    { label: 'Protocol-led therapy selection', icon: Network },
    { label: 'Guided delivery', icon: UserCheck },
    { label: 'Tracked outcomes', icon: LineChart },
    { label: 'Recurring memberships', icon: Repeat },
];

function ModelItem({ label, icon: Icon, variant, delay }) {
    const isAgeon = variant === 'ageon';

    return (
        <li
            className={`flex items-center gap-3.5 rounded-xl px-4 py-3.5 ${
                isAgeon
                    ? 'bg-white/10 ring-1 ring-white/15'
                    : 'bg-white ring-1 ring-ink-100'
            }`}
            data-aos="fade-up"
            data-aos-delay={String(delay)}
        >
            <span
                className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${
                    isAgeon
                        ? 'bg-brand-teal/25 text-brand-teal'
                        : 'bg-ink-50 text-ink-400'
                }`}
            >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <span className={`flex-1 text-sm font-medium ${isAgeon ? 'text-white/90' : 'text-ink-600'}`}>
                {label}
            </span>
            <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${
                    isAgeon ? 'bg-brand-teal text-white' : 'bg-ink-100 text-ink-400'
                }`}
            >
                {isAgeon ? (
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                ) : (
                    <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                )}
            </span>
        </li>
    );
}

export default function LandingAgeonDifference() {
    return (
        <section id="ageon-difference" className="relative scroll-mt-24 overflow-hidden landing-mesh-hero border-t border-ink-100/80">
            <div aria-hidden className="pointer-events-none absolute inset-0 landing-grain opacity-35" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <div className="max-w-3xl" data-aos="fade-up">
                    <span className="lp-chip mb-6">The AGEON difference</span>
                    <h2 className="font-heading text-3xl font-extrabold leading-[1.12] tracking-tight text-ink-900 sm:text-4xl lg:text-[2.65rem]">
                        Machines are not the moat.{' '}
                        <span className="bg-gradient-to-r from-brand-teal to-brand-teal-700 bg-clip-text text-transparent">
                            Protocols are.
                        </span>
                    </h2>
                </div>

                <div className="mt-12 grid grid-cols-1 gap-6 lg:mt-14 lg:grid-cols-2 lg:gap-8">
                    {/* Conventional */}
                    <div
                        className="rounded-3xl bg-white/80 p-6 ring-1 ring-ink-100 backdrop-blur-sm sm:p-8"
                        data-aos="fade-right"
                    >
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink-400">
                            Conventional model
                        </p>
                        <ul className="mt-5 space-y-2.5">
                            {CONVENTIONAL.map((item, i) => (
                                <ModelItem
                                    key={item.label}
                                    label={item.label}
                                    icon={item.icon}
                                    variant="conventional"
                                    delay={80 + i * 50}
                                />
                            ))}
                        </ul>
                    </div>

                    {/* AGEON */}
                    <div
                        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-ink-900 via-[#0a2e28] to-brand-teal-700 p-6 shadow-[0_32px_64px_-24px_rgba(0,140,124,0.45)] sm:p-8"
                        data-aos="fade-left"
                        data-aos-delay="100"
                    >
                        <div aria-hidden className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-brand-gold/15 blur-3xl" />
                        <p className="relative text-[11px] font-bold uppercase tracking-[0.18em] text-white/50">
                            The AGEON model
                        </p>
                        <ul className="relative mt-5 space-y-2.5">
                            {AGEON_MODEL.map((item, i) => (
                                <ModelItem
                                    key={item.label}
                                    label={item.label}
                                    icon={item.icon}
                                    variant="ageon"
                                    delay={140 + i * 50}
                                />
                            ))}
                        </ul>
                    </div>
                </div>

                <div
                    className="mt-10 text-center lg:mt-12"
                    data-aos="fade-up"
                    data-aos-delay="200"
                >
                    <p className="font-heading text-lg font-semibold text-ink-800 sm:text-xl">
                        From{' '}
                        <span className="text-ink-500">fragmented sessions</span>
                        {' '}to{' '}
                        <span className="bg-gradient-to-r from-brand-teal to-brand-teal-700 bg-clip-text text-transparent">
                            structured smart-ageing journeys.
                        </span>
                    </p>
                </div>
            </div>
        </section>
    );
}
